// Browser UI functionality
import S3Browser from './s3.js';

class BrowserUI {
    constructor(auth) {
        this.s3Browser = new S3Browser(auth);
        this.currentItems = [];
        this.isLoading = false;
    }

    // Initialize the browser UI
    async init() {
        this.bindEvents();

        // Restore previous path from session storage or start at root
        const savedPath = sessionStorage.getItem('currentBrowserPath') || '';
        await this.loadPath(savedPath);
    }

    // Bind event handlers
    bindEvents() {
        // Breadcrumb navigation
        document.addEventListener('click', (e) => {
            if (e.target.matches('[data-breadcrumb-path]')) {
                e.preventDefault();
                const path = e.target.getAttribute('data-breadcrumb-path');
                this.loadPath(path);
            }
        });

        // File/folder navigation - handle clicks on file names
        document.addEventListener('click', async (e) => {
            if (e.target.matches('.file-link') || e.target.closest('.file-link')) {
                const element = e.target.matches('.file-link') ? e.target : e.target.closest('.file-link');
                const path = element.getAttribute('data-path');
                const type = element.getAttribute('data-type');
                const fileType = element.getAttribute('data-file-type');

                e.preventDefault();

                try {
                    if (type === 'folder') {
                        await this.loadPath(path);
                    } else {
                        // Handle default action for files based on type
                        switch (fileType) {
                            case 'image':
                                await this.viewFile(path);
                                break;
                            case 'video':
                            case 'audio':
                                await this.playMedia(path);
                                break;
                            default:
                                await this.openRawFile(path);
                                break;
                        }
                    }
                } catch (error) {
                    console.error('Error performing default action:', error);
                }
            }
        });

        // Raw file links
        document.addEventListener('click', async (e) => {
            if (e.target.matches('.raw-link')) {
                e.preventDefault();
                const path = e.target.getAttribute('data-path');

                if (!path) return;

                try {
                    await this.openRawFile(path);
                } catch (error) {
                    console.error('Error handling raw file action:', error);
                    this.showError(`Failed to open raw file: ${error.message}`);
                }
            }
        });

        // Slideshow button
        document.addEventListener('click', async (e) => {
            if (e.target.matches('#slideshow-btn')) {
                e.preventDefault();
                await this.openSlideshow();
            }
        });

    }

    // Load items for a specific path
    async loadPath(path, append = false) {
        if (this.isLoading) return;

        this.isLoading = true;
        this.showLoading();

        try {
            const result = await this.s3Browser.navigateTo(path);
            this.currentItems = [...result.folders, ...result.files];

            // Save current path to session storage
            sessionStorage.setItem('currentBrowserPath', path);

            this.updateBreadcrumb(path);
            this.renderItems();
            this.updateStatus();

        } catch (error) {
            console.error('Error loading path:', error);
            this.showError('Failed to load directory: ' + error.message);
        } finally {
            this.isLoading = false;
            this.hideLoading();
        }
    }


    // Update breadcrumb navigation
    updateBreadcrumb(path) {
        const breadcrumbNav = document.querySelector('.breadcrumb ol');
        if (!breadcrumbNav) return;

        const breadcrumbs = this.s3Browser.parsePath(path);

        let html = '<li><a href="#" data-breadcrumb-path="">Home</a></li>';

        breadcrumbs.forEach((crumb, index) => {
            if (index === breadcrumbs.length - 1) {
                html += `<li>${crumb.name}</li>`;
            } else {
                html += `<li><a href="#" data-breadcrumb-path="${crumb.path}">${crumb.name}</a></li>`;
            }
        });

        breadcrumbNav.innerHTML = html;
    }

    // Render the file/folder list
    renderItems() {
        const tbody = document.querySelector('.file-browser tbody');
        if (!tbody) return;

        if (this.currentItems.length === 0) {
            tbody.innerHTML = '<tr><td colspan="2" style="text-align: center; color: var(--muted-color);">No files or folders found</td></tr>';
            return;
        }

        const html = this.currentItems.map(item => {
            const fileType = item.type === 'folder' ? 'folder' : this.s3Browser.getFileType(item.extension);

            // Only show raw link for files (not folders)
            let actions = '';
            if (item.type === 'file') {
                actions = '<a href="#" class="raw-link" data-path="' + item.path + '">Raw</a>';
            }

            const folderIcon = item.type === 'folder' ? '<span class="file-icon">📁</span>' : '';

            return `
                <tr>
                    <td style="word-break: break-word; width: 100%;">
                        ${folderIcon}
                        <a href="#" data-path="${item.path}" data-type="${item.type}" data-file-type="${fileType}" class="file-link">${item.name}</a>
                    </td>
                    <td style="white-space: nowrap; width: 1%;">${actions}</td>
                </tr>
            `;
        }).join('');

        tbody.innerHTML = html;
    }

    // Update status bar
    updateStatus() {
        const statusBar = document.querySelector('.status-bar small');
        if (!statusBar) return;

        const folderCount = this.currentItems.filter(item => item.type === 'folder').length;
        const fileCount = this.currentItems.filter(item => item.type === 'file').length;

        statusBar.textContent = `${this.currentItems.length} items (${folderCount} folders, ${fileCount} files)`;

        // Update slideshow controls
        this.updateSlideshowControls();
    }

    // Update slideshow controls visibility
    updateSlideshowControls() {
        const slideshowControls = document.getElementById('slideshow-controls');
        if (!slideshowControls) return;

        // Check if there are any images in the current folder
        const imageItems = this.currentItems.filter(item =>
            item.type === 'file' && this.s3Browser.getFileType(item.extension) === 'image'
        );

        if (imageItems.length > 0) {
            slideshowControls.style.display = 'block';
            const button = document.getElementById('slideshow-btn');
            if (button) {
                button.textContent = 'View Slideshow';
            }
        } else {
            slideshowControls.style.display = 'none';
        }
    }

    // File action handlers
    async viewFile(path) {
        try {
            const signedUrl = await this.s3Browser.getSignedUrl(path);
            window.open(signedUrl, '_blank');
        } catch (error) {
            throw new Error('Could not generate view URL');
        }
    }

    async playMedia(path) {
        try {
            // Save current browser path before navigating to player
            const currentPath = sessionStorage.getItem('currentBrowserPath') || '';
            sessionStorage.setItem('returnPath', currentPath);

            // Navigate to video player page with path parameter
            const encodedPath = encodeURIComponent(path);
            window.location.href = `player.html?path=${encodedPath}`;
        } catch (error) {
            throw new Error('Could not navigate to video player');
        }
    }

    async openRawFile(path) {
        try {
            const signedUrl = await this.s3Browser.getSignedUrl(path);
            window.open(signedUrl, '_blank');
        } catch (error) {
            throw new Error('Could not generate raw file URL');
        }
    }

    async openSlideshow() {
        try {
            // Get all image files from current folder
            const imageItems = this.currentItems.filter(item =>
                item.type === 'file' && this.s3Browser.getFileType(item.extension) === 'image'
            );

            if (imageItems.length === 0) {
                this.showError('No images found in current folder');
                return;
            }

            // Save current browser path for return navigation
            const currentPath = sessionStorage.getItem('currentBrowserPath') || '';
            sessionStorage.setItem('returnPath', currentPath);

            // Prepare slideshow data
            const slideshowData = {
                images: imageItems.map(item => ({
                    path: item.path,
                    name: item.name
                })),
                currentIndex: 0
            };

            // Store slideshow data
            sessionStorage.setItem('slideshowData', JSON.stringify(slideshowData));

            // Navigate to slideshow page
            window.location.href = 'slideshow.html';

        } catch (error) {
            console.error('Error opening slideshow:', error);
            this.showError('Failed to open slideshow: ' + error.message);
        }
    }

    // UI state methods
    showLoading() {
        const tbody = document.querySelector('.file-browser tbody');
        if (tbody) {
            tbody.innerHTML = '<tr><td colspan="2" style="text-align: center; padding: 2rem;"><span>Loading...</span></td></tr>';
        }
    }

    hideLoading() {
        // Loading state is replaced by renderItems()
    }

    showError(message) {
        const tbody = document.querySelector('.file-browser tbody');
        if (tbody) {
            tbody.innerHTML = `<tr><td colspan="2" style="text-align: center; padding: 2rem; color: var(--del-color);">${message}</td></tr>`;
        }
    }
}

export default BrowserUI;